// @ts-nocheck
// Agent Portal (/cti2) — idle-time tracking, split out of AgentPortalPage.tsx.
// Ticks idleSeconds while `screen === 'idle'`, and reports the idle stretch
// to API_AGENT_IDLE_STATS once it ends — either because a call started
// (the oncall-transition effect below) or because the agent logged out
// (Topbar.tsx calls sendAgentIdleStats directly via the onBeforeLogout
// prop AgentPortalPage.tsx wires up — this hook doesn't know about logout
// itself, it just exposes the same send function for that call site to use).
import { useCallback, useEffect, useState } from 'react';
import { useCallStore } from '@/router/zustand/callStore';
import { getIdleSeconds } from '@/features/cti/ctiHelpers';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_IDLE_STATS } from '@/service/url';

export function useIdleTracking({ screen, callState, loggedUserId }) {
  const webrtcIdleTime = useCallStore((s) => s.webrtcPhoneStartTime);
  const idleSessionStartedAt = useCallStore((s) => s.idleSessionStartedAt);

  const [idleStartedAt, setIdleStartedAt] = useState(() => new Date().toISOString());
  const [idleSeconds, setIdleSeconds] = useState(0);

  // `screen` alone lags here — useScreenState.ts keeps it 'idle' through
  // the moment Call is pressed until outgoingRingingActive flips true a
  // beat later, so the idle counter kept ticking through that gap instead
  // of resetting the instant the agent dialed out. `callState` flips to
  // 'dialing' immediately, so combining both catches that gap while still
  // correctly NOT resuming idle ticking during wrap-up/disposition (screen
  // is 'disposition' there even though callState has already gone back to
  // 'idle' once the call itself ended).
  const isTrulyIdle = screen === 'idle' && callState === 'idle';

  useEffect(() => {
    if (isTrulyIdle) setIdleStartedAt(new Date().toISOString());
  }, [isTrulyIdle]);

  useEffect(() => {
    if (!isTrulyIdle) { setIdleSeconds(0); return undefined; }
    const startAt = idleSessionStartedAt || webrtcIdleTime;
    if (!startAt) { setIdleSeconds(0); return undefined; }
    setIdleSeconds(getIdleSeconds(startAt));
    const interval = setInterval(() => setIdleSeconds(getIdleSeconds(startAt)), 1000);
    return () => clearInterval(interval);
  }, [idleSessionStartedAt, isTrulyIdle, webrtcIdleTime]);

  const sendAgentIdleStats = useCallback(async (toOverride?: any) => {
    if (!loggedUserId) return;
    const { idleSessionStartedAt: startedAt, webrtcPhoneStartTime, incomingReceivedAt, idleStatsSentForIncomingAt } = useCallStore.getState();
    if (incomingReceivedAt && idleStatsSentForIncomingAt === incomingReceivedAt) return;
    const from = startedAt || webrtcPhoneStartTime || idleStartedAt;
    const to = toOverride || incomingReceivedAt || new Date().toISOString();
    if (!from) return;
    try {
      await postApiCall(API_AGENT_IDLE_STATS, { from, to, agent_id: loggedUserId });
      if (incomingReceivedAt) useCallStore.getState().markIdleStatsSentForIncomingAt(incomingReceivedAt);
    } catch (error) { console.error('Failed to save agent idle stats', error); }
  }, [idleStartedAt, loggedUserId]);

  useEffect(() => {
    if (callState !== 'oncall') return;
    sendAgentIdleStats(useCallStore.getState().incomingReceivedAt || new Date().toISOString());
    useCallStore.getState().setAnswering(false);
  }, [callState, sendAgentIdleStats]);

  return { idleSeconds, sendAgentIdleStats };
}
